import type { ProvedorOAuth } from '@locaveri/shared'
import type { ReactElement } from 'react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { irParaLoginSocial, useProvedoresOAuth } from '@/features/auth/api'

/**
 * Botões de login social.
 *
 * ---------------------------------------------------------------------------
 * O PROVEDOR NÃO CONFIGURADO SIMPLESMENTE NÃO APARECE
 * ---------------------------------------------------------------------------
 * As credenciais de Google e Apple ainda não existem (ver ROADMAP). Quem sabe
 * o que está habilitado é a API, que lê o próprio ambiente; o painel apenas
 * pergunta. Enquanto a resposta vier vazia — ou a consulta falhar, ou a API
 * estiver fora do ar — nenhum botão é renderizado e a tela de login continua
 * inteira. Um botão que leva a uma tela de erro do Google é pior do que botão
 * nenhum.
 *
 * Nada aqui é desabilitado "por enquanto": ou o caminho existe de verdade, ou
 * ele não é oferecido.
 */

const ROTULOS: Readonly<Record<ProvedorOAuth, string>> = {
  GOOGLE: 'Continuar com Google',
  APPLE: 'Continuar com Apple',
}

function IconeGoogle(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3.01c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.26v3.09A11.995 11.995 0 0 0 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.63H1.26a12 12 0 0 0 0 10.74l4.01-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.26 6.63l4.01 3.09C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  )
}

function IconeApple(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4" fill="currentColor">
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.13 3-.79.9-2.06 1.6-3.13 1.51-.13-1.1.42-2.26 1.11-3 .78-.85 2.14-1.48 3.15-1.51zM20.9 17.1c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.1 3.51-1.53.02-1.93-1-4.01-.99-2.08.01-2.51 1.01-4.05.99-1.72-.02-3.03-1.77-4.02-3.32-2.77-4.35-3.06-9.45-1.35-12.16 1.21-1.92 3.13-3.05 4.93-3.05 1.84 0 2.99 1.01 4.51 1.01 1.47 0 2.37-1.01 4.49-1.01 1.61 0 3.31.88 4.53 2.39-3.98 2.18-3.34 7.87.6 9.67z" />
    </svg>
  )
}

export interface BotoesDeLoginSocialProps {
  readonly desabilitado?: boolean
}

export function BotoesDeLoginSocial({
  desabilitado = false,
}: BotoesDeLoginSocialProps): ReactElement | null {
  const { data: provedores } = useProvedoresOAuth()

  if (provedores === undefined || provedores.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs uppercase tracking-wide text-fg-muted">ou</span>
        <Separator className="flex-1" />
      </div>

      {provedores.map((provedor) => (
        <Button
          key={provedor}
          variant="secondary"
          className="w-full"
          disabled={desabilitado}
          onClick={() => {
            irParaLoginSocial(provedor)
          }}
        >
          {provedor === 'GOOGLE' ? <IconeGoogle /> : <IconeApple />}
          {ROTULOS[provedor]}
        </Button>
      ))}
    </div>
  )
}
